import React, { useState } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Flame,
  Box,
  Grid
} from "lucide-react";
import SignInModal from "./SignInModal";
import CreateAccountModal from "./CreateAccountModal";

// Import internal mini-pages
import YourAccountPage from "../pages/YourAccountPage";
import TrackOrdersPage from "../pages/TrackOrdersPage";
import CustomerServicePage from "../pages/CustomerServicePage";

const SideMenu = ({ onClose }) => {
  const [activeMenu, setActiveMenu] = useState("main");
  const [showSignIn, setShowSignIn] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Define all menu sections and submenus
  const menus = {
    main: {
      title: "Main Menu",
      sections: [
        {
          title: "Departments",
          icon: <Box size={18} className="text-amber-600" />,
          items: [
            { name: "Grinders & Crushers", key: "grinders" },
            { name: "Rolling Papers & Cones", key: "rolling" },
            { name: "Vapes & E-Juice", key: "vapes" },
            { name: "Glass Bongs & Rigs", key: "bongs" },
            { name: "Hookahs & Accessories", key: "hookah" },
            { name: "Ashtrays & Trays", key: "ashtrays" },
            { name: "Storage & Containers", key: "storage" }
          ]
        },
        {
          title: "Products",
          icon: <Flame size={18} className="text-amber-600" />,
          items: [
            { name: "Best Sellers", key: "bestsellers" },
            { name: "New Arrivals", key: "newreleases" },
            { name: "Trending Items", key: "trending" },
            { name: "Bulk Deals", key: "bulk" },
            { name: "Clearance Offers", key: "clearance" }
          ]
        },
        {
          title: "Categories",
          icon: <Grid size={18} className="text-amber-600" />,
          items: [
            { name: "Smoking Kits", key: "kits" },
            { name: "Lighters & Torches", key: "lighters" },
            { name: "Cleaning Tools", key: "cleaning" },
            { name: "Rolling Machines", key: "machines" },
            { name: "Odor-Proof Bags", key: "odor" },
            { name: "Smoky Apparel", key: "apparel" },
            { name: "Candy & Munchies", key: "candy" }
          ]
        },
        {
          title: "Help & Settings",
          items: [
            { name: "Your Account", page: "account" },
            { name: "Track Orders", page: "trackOrders" },
            { name: "Customer Service", page: "customerService" },
            { name: "Sign In", key: "signin" }
          ]
        }
      ]
    },

    // Submenus
    grinders: {
      title: "Grinders & Crushers",
      items: [
        { name: "Metal Grinders" },
        { name: "Acrylic Grinders" },
        { name: "Wooden Grinders" },
        { name: "Electric Grinders" },
        { name: "Mini Portable Grinders" }
      ]
    },
    rolling: {
      title: "Rolling Papers & Cones",
      items: [
        { name: "King Size Papers" },
        { name: "Organic Hemp Papers" },
        { name: "Pre-Rolled Cones" },
        { name: "Flavored Papers" },
        { name: "Rolling Trays" },
        { name: "Rolling Machines" }
      ]
    },
    vapes: {
      title: "Vapes & E-Juice",
      items: [
        { name: "Disposable Vapes" },
        { name: "Pod Systems" },
        { name: "Vape Juice / E-Liquid" },
        { name: "Vape Coils & Tanks" },
        { name: "Chargers & Batteries" }
      ]
    },
    bongs: {
      title: "Glass Bongs & Rigs",
      items: [
        { name: "Beaker Bongs" },
        { name: "Percolator Bongs" },
        { name: "Mini Bongs" },
        { name: "Recycler Rigs" },
        { name: "Ash Catchers" }
      ]
    },
    hookah: {
      title: "Hookahs & Accessories",
      items: [
        { name: "Classic Hookahs" },
        { name: "Portable Hookahs" },
        { name: "Hookah Charcoal" },
        { name: "Hookah Flavors" },
        { name: "Hoses & Bowls" }
      ]
    },
    ashtrays: {
      title: "Ashtrays & Rolling Trays",
      items: [
        { name: "Glass Ashtrays" },
        { name: "Metal Ashtrays" },
        { name: "Ceramic Ashtrays" },
        { name: "Glow Ashtrays" },
        { name: "Custom Printed Trays" }
      ]
    },
    storage: {
      title: "Storage & Containers",
      items: [
        { name: "Smell-Proof Jars" },
        { name: "Stash Boxes" },
        { name: "Zip Bags" },
        { name: "Vacuum Containers" },
        { name: "Display Cases" }
      ]
    },
    bestsellers: {
      title: "Best Sellers",
      items: [
        { name: "Top Metal Grinders" },
        { name: "Popular Vape Pens" },
        { name: "Most Loved Rolling Papers" },
        { name: "Best Glass Bongs" },
        { name: "Top Rated Lighters" }
      ]
    },
    newreleases: {
      title: "New Arrivals",
      items: [
        { name: "2025 Edition Grinders" },
        { name: "Latest Vape Devices" },
        { name: "New Organic Hemp Papers" },
        { name: "New Arrival Glass Bongs" }
      ]
    },
    trending: {
      title: "Trending Items",
      items: [
        { name: "Glow-in-Dark Bongs" },
        { name: "Smart Electric Grinders" },
        { name: "Refillable Vape Pods" },
        { name: "Limited Edition Lighters" }
      ]
    },
    bulk: {
      title: "Bulk Deals",
      items: [
        { name: "Wholesale Papers" },
        { name: "Bulk Grinders" },
        { name: "Bulk Lighters" },
        { name: "Dealer Kits" }
      ]
    },
    clearance: {
      title: "Clearance Offers",
      items: [
        { name: "Last Chance Items" },
        { name: "End of Line Stock" },
        { name: "Discounted Bundles" }
      ]
    },
    kits: {
      title: "Smoking Kits",
      items: [
        { name: "Starter Kits" },
        { name: "Retail Bundles" },
        { name: "Gift Packs" },
        { name: "Custom Kits" }
      ]
    },
    lighters: {
      title: "Lighters & Torches",
      items: [
        { name: "Butane Torches" },
        { name: "Refillable Lighters" },
        { name: "Clipper Lighters" },
        { name: "Novelty Lighters" }
      ]
    },
    cleaning: {
      title: "Cleaning Tools",
      items: [
        { name: "Brushes" },
        { name: "Cleaning Solutions" },
        { name: "Pipe Cleaners" },
        { name: "Replacement Parts" }
      ]
    },
    machines: {
      title: "Rolling Machines",
      items: [
        { name: "Manual Rolling Machines" },
        { name: "Electric Rolling Machines" },
        { name: "Spare Parts" }
      ]
    },
    odor: {
      title: "Odor-Proof Bags",
      items: [
        { name: "Small Zip Bags" },
        { name: "Smell Proof Jars" },
        { name: "Large Storage Bags" }
      ]
    },
    apparel: {
      title: "Smoky Apparel",
      items: [
        { name: "Hats" },
        { name: "T-Shirts" },
        { name: "Hoodies" }
      ]
    },
    candy: {
      title: "Candy & Munchies",
      items: [
        { name: "Gummies" },
        { name: "Chocolates" },
        { name: "Snacks" }
      ]
    }
  };

  const menu = menus[activeMenu];

  // Render helper for special pages
  const renderPage = () => {
    switch (activeMenu) {
      case "account":
        return <YourAccountPage onBack={() => setActiveMenu("main")} />;
      case "trackOrders":
        return <TrackOrdersPage onBack={() => setActiveMenu("main")} />;
      case "customerService":
        return <CustomerServicePage onBack={() => setActiveMenu("main")} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        ></div>

        {/* Panel */}
        <div className="relative bg-white w-80 h-full overflow-y-auto shadow-2xl z-50 transform transition-transform duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900 text-white">
            {activeMenu !== "main" ? (
              <button
                onClick={() => setActiveMenu("main")}
                className="flex items-center gap-1 text-sm"
              >
                <ChevronLeft size={20} /> Back
              </button>
            ) : (
              <h2
                className="text-lg font-semibold cursor-pointer"
                onClick={() => setShowSignIn(true)}
              >
                Hello, sign in
              </h2>
            )}
            <button onClick={onClose}>
              <X size={22} />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {activeMenu === "main" ? (
              <>
                {menu.sections.map((section, i) => (
                  <div key={i} className="mb-6 border-b pb-3">
                    <h3 className="flex items-center gap-2 text-sm font-bold text-gray-600 mb-2 uppercase tracking-wide">
                      {section.icon}
                      {section.title}
                    </h3>
                    <ul className="space-y-2">
                      {section.items.map((item, j) => (
                        <li
                          key={j}
                          className="flex justify-between items-center py-2 px-2 rounded-md hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            if (item.key === "signin") {
                              setShowSignIn(true);
                              return;
                            }
                            if (item.page) {
                              setActiveMenu(item.page);
                              return;
                            }
                            if (menus[item.key]) {
                              setActiveMenu(item.key);
                            } else {
                              alert(`Opening: ${item.name}`);
                            }
                          }}
                        >
                          <span>{item.name}</span>
                          {(menus[item.key] || item.page) && <ChevronRight size={18} />}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            ) : menus[activeMenu] ? (
              <>
                <h3 className="text-lg font-bold text-gray-800 mb-3">
                  {menus[activeMenu].title}
                </h3>
                <ul className="space-y-2">
                  {menus[activeMenu].items.map((sub, i) => (
                    <li
                      key={i}
                      className="py-2 px-3 hover:bg-gray-100 rounded-md cursor-pointer"
                      onClick={() => alert(`Selected: ${sub.name}`)}
                    >
                      {sub.name}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              renderPage()
            )}
          </div>
        </div>
      </div>

      {/* Sign In Modal */}
      {showSignIn && (
        <SignInModal
          onClose={() => setShowSignIn(false)}
          onSwitch={(view) => {
            setShowSignIn(false);
            if (view === "create") setShowCreate(true);
          }}
        />
      )}

      {/* Create Account Modal */}
      {showCreate && (
        <CreateAccountModal
          onClose={() => setShowCreate(false)}
          onSwitch={(view) => {
            setShowCreate(false);
            if (view === "signin") setShowSignIn(true);
          }}
        />
      )}
    </>
  );
};

export default SideMenu;
